import React, { useEffect, useRef } from 'react';
import { ExternalLink, ShoppingCart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { trackImpression, trackClick } from '../../services/promotionTracking';
import API_BASE_URL from '../../config/api';

/**
 * Composant pour afficher une promotion dans la section promotions
 * Supporte les layouts dish (image/vidéo en haut, texte en bas) et menu (image à gauche, texte à droite)
 */
const PromotionCard = ({ promotion, onOrder }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasTrackedImpression = useRef(false);

  // Tracker l'impression quand le composant est monté (uniquement pour les promotions réelles)
  useEffect(() => {
    if (promotion?.id && !hasTrackedImpression.current) {
      // Ne tracker que si l'ID est numérique (pas une promotion de démo)
      const promotionId = typeof promotion.id === 'number' ? promotion.id : parseInt(promotion.id);
      if (!isNaN(promotionId) && promotionId > 0) {
        trackImpression(promotionId, 'section');
        hasTrackedImpression.current = true;
      }
    }
  }, [promotion?.id]);

  const handleCTA = () => {
    // Tracker le clic (uniquement pour les promotions réelles)
    if (promotion?.id) {
      const promotionId = typeof promotion.id === 'number' ? promotion.id : parseInt(promotion.id);
      if (!isNaN(promotionId) && promotionId > 0) {
        trackClick(promotionId, 'section', {
          cta_type: promotion.cta_type,
          action: promotion.cta_type === 'order' ? 'order_click' : 'discover_click',
        });
      }
    }

    const getPromotedDishId = () => {
      if (Array.isArray(promotion?.dish_ids) && promotion.dish_ids.length > 0) {
        return Number(promotion.dish_ids[0]) || null;
      }
      if (Array.isArray(promotion?.dishes) && promotion.dishes.length > 0) {
        const firstDishLink = promotion.dishes[0];
        const linkedDishId = Number(firstDishLink?.dish_id || firstDishLink?.dish?.id);
        return Number.isFinite(linkedDishId) ? linkedDishId : null;
      }
      return null;
    };

    const getPromotedMenuId = () => {
      if (Array.isArray(promotion?.menu_ids) && promotion.menu_ids.length > 0) {
        return Number(promotion.menu_ids[0]) || null;
      }
      if (Array.isArray(promotion?.menus) && promotion.menus.length > 0) {
        const firstMenuLink = promotion.menus[0];
        const linkedMenuId = Number(firstMenuLink?.menu_id || firstMenuLink?.menu?.id);
        return Number.isFinite(linkedMenuId) ? linkedMenuId : null;
      }
      return null;
    };

    const navigateToPromotionTarget = () => {
      const ctaUrl = (promotion?.cta_url || '').trim();
      if (ctaUrl) {
        if (/^https?:\/\//i.test(ctaUrl)) {
          window.open(ctaUrl, '_blank', 'noopener,noreferrer');
          return true;
        }
        navigate(ctaUrl.startsWith('/') ? ctaUrl : `/${ctaUrl}`);
        return true;
      }

      const dishId = getPromotedDishId();
      if (dishId) {
        navigate(`/product/${dishId}`);
        return true;
      }

      const menuId = getPromotedMenuId();
      if (menuId && promotion.restaurant_id) {
        navigate(`/restaurant/${promotion.restaurant_id}?menu=${menuId}`);
        return true;
      }

      return false;
    };

    if (promotion.cta_type === 'order' && onOrder) {
      onOrder(promotion);
      return;
    }

    if (navigateToPromotionTarget()) {
      return;
    }

    if (promotion.restaurant_id) {
      navigate(`/restaurant/${promotion.restaurant_id}`);
    } else {
      console.warn('Promotion restaurant_id manquant:', promotion);
      navigate('/');
    }
  };

  if (!promotion) return null;

  const normalizeMediaUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    const trimmed = url.trim();
    if (!trimmed) return null;
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    const base = (API_BASE_URL || '').replace(/\/$/, '');
    return trimmed.startsWith('/') ? `${base}${trimmed}` : `${base}/${trimmed}`;
  };

  // Luminance relative du fond pour garantir le contraste du bouton CTA
  const getLuminance = (hex) => {
    if (!hex || typeof hex !== 'string') return 1;
    const clean = hex.replace('#', '');
    const full = clean.length === 3 ? clean.split('').map((ch) => ch + ch).join('') : clean;
    if (full.length !== 6 || /[^0-9a-fA-F]/.test(full)) return 1;
    const r = parseInt(full.slice(0, 2), 16) / 255;
    const g = parseInt(full.slice(2, 4), 16) / 255;
    const b = parseInt(full.slice(4, 6), 16) / 255;
    const lin = (v) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
    return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  };
  const isDarkBackground = getLuminance(promotion.background_color) < 0.5;
  const ctaButtonClass = isDarkBackground
    ? 'bg-white text-gray-900 hover:bg-gray-100'
    : 'bg-gray-900 text-white hover:bg-gray-800';
  // Couleurs de texte adaptées au fond de la promotion pour rester lisibles
  const titleTextClass = isDarkBackground ? 'text-white' : 'text-gray-900';
  const bodyTextClass = isDarkBackground ? 'text-gray-300' : 'text-gray-900 font-normal';
  const mutedTextClass = isDarkBackground ? 'text-gray-400' : 'text-gray-900 font-normal';
  const priceTextClass = isDarkBackground ? 'text-emerald-400' : 'text-green-600';

  const imageUrl = normalizeMediaUrl(promotion.media_url || promotion.image_url);
  const videoUrl = normalizeMediaUrl(promotion.video_url || (promotion.media_type === 'video' ? promotion.media_url : null));
  const shortDescription = promotion.short_description || promotion.description;
  const ctaText = promotion.cta_label || promotion.cta_text;
  const bgColor = promotion.background_color || '#FFFFFF';
  // layout_type peut ne pas être retourné par l'API — on le dérive des relations si absent
  const layoutType = promotion.layout_type || (promotion.menus?.length > 0 ? 'menu' : 'dish');
  // cta_type peut ne pas être retourné par l'API — on le dérive du label
  const ctaType = promotion.cta_type || (
    ctaText && /commander|order|acheter/i.test(ctaText) ? 'order' : 'discover'
  );

  const handleImgError = (e) => {
    e.target.style.display = 'none';
  };

  // Layout pour plat : image/vidéo en haut, texte en bas
  if (layoutType === 'dish') {
  return (
    <div
        className="rounded-lg shadow-lg overflow-hidden transition-shadow relative"
        style={{ backgroundColor: bgColor }}
      >
        {/* Label */}
        {promotion.label && (
          <div className="absolute top-2 left-2 z-10">
            <span className={`px-2 py-1 rounded text-xs font-bold ${
              promotion.label === 'offre_du_jour' 
                ? 'bg-yellow-500 text-white' 
                : 'bg-green-500 text-white'
            }`}>
              {promotion.label === 'offre_du_jour' ? t('promotions.dealOfTheDay') : t('promotions.new')}
            </span>
          </div>
        )}
        
        {/* Média (image ou vidéo) */}
        <div className="relative w-full h-48 overflow-hidden">
          {promotion.media_type === 'video' && videoUrl ? (
            <video
              src={videoUrl}
              autoPlay
              loop
              muted
              className="w-full h-full object-cover"
            />
          ) : imageUrl ? (
            <img
              src={imageUrl}
              alt={promotion.title}
              className="w-full h-full object-cover"
              onError={handleImgError}
            />
          ) : null}
      </div>

      {/* Contenu */}
        <div className="p-4">
          <h3 className={`font-bold text-xl mb-2 ${titleTextClass}`}>{promotion.title}</h3>
          {shortDescription && (
            <p className={`text-sm mb-3 line-clamp-2 ${bodyTextClass}`}>
              {shortDescription}
          </p>
        )}

        {/* Prix */}
          <div className="flex items-center space-x-2 mb-4">
            <span className={`text-2xl font-bold ${priceTextClass}`}>
              {promotion.final_price != null ? Number(promotion.final_price).toFixed(2) : '—'}€
            </span>
            {promotion.original_price != null && promotion.final_price != null && promotion.original_price > promotion.final_price && (
              <span className={`text-sm line-through ${mutedTextClass}`}>
                {Number(promotion.original_price).toFixed(2)}€
                </span>
              )}
          </div>

          {/* Bouton CTA */}
          <button
            onClick={handleCTA}
            className={`w-full py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2 ${ctaButtonClass}`}
          >
            {ctaType === 'order' ? (
              <>
                <ShoppingCart size={16} />
                <span>{ctaText || t('promotions.order')}</span>
              </>
            ) : (
              <>
                <ExternalLink size={16} />
                <span>{ctaText || t('promotions.discover')}</span>
              </>
            )}
          </button>
      </div>
    </div>
  );
  }

  // Layout pour menu : image à gauche, texte à droite
  return (
    <div
      className="rounded-lg shadow-lg overflow-hidden transition-shadow flex"
      style={{ backgroundColor: bgColor }}
    >
      {/* Média (image ou vidéo) à gauche */}
      <div className="relative w-1/3 h-64 overflow-hidden">
        {promotion.media_type === 'video' && videoUrl ? (
          <video
            src={videoUrl}
            autoPlay
            loop
            muted
            className="w-full h-full object-cover"
          />
        ) : imageUrl ? (
          <img
            src={imageUrl}
            alt={promotion.title}
            className="w-full h-full object-cover"
            onError={handleImgError}
          />
        ) : null}
      </div>

      {/* Contenu à droite */}
      <div className="flex-1 p-6 flex flex-col justify-between">
        <div>
          {/* Label */}
          {promotion.label && (
            <span className={`inline-block px-2 py-1 rounded text-xs font-bold mb-2 ${
              promotion.label === 'offre_du_jour' 
                ? 'bg-yellow-500 text-white' 
                : 'bg-green-500 text-white'
            }`}>
              {promotion.label === 'offre_du_jour' ? t('promotions.dealOfTheDay') : t('promotions.new')}
                </span>
              )}

          <h3 className={`font-bold text-2xl mb-2 ${titleTextClass}`}>{promotion.title}</h3>
          {shortDescription && (
            <p className={`text-sm mb-4 ${bodyTextClass}`}>
              {shortDescription}
            </p>
          )}

          {/* Liste des éléments du menu (si disponible) */}
          {promotion.menus && promotion.menus.length > 0 && (
            <div className="mb-4">
              <h4 className={`font-semibold mb-2 ${titleTextClass}`}>{t('promotions.menuContents')}</h4>
              <ul className={`list-disc list-inside text-sm space-y-1 ${bodyTextClass}`}>
                {(promotion.menus[0].menu?.dishes || []).slice(0, 5).map((dish, index) => (
                  <li key={index}>{dish.name}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Prix */}
          <div className="flex items-center space-x-2 mb-4">
            <span className={`text-3xl font-bold ${priceTextClass}`}>
              {promotion.final_price != null ? Number(promotion.final_price).toFixed(2) : '—'}€
            </span>
            {promotion.original_price != null && promotion.final_price != null && promotion.original_price > promotion.final_price && (
              <span className={`text-lg line-through ${mutedTextClass}`}>
                {Number(promotion.original_price).toFixed(2)}€
              </span>
            )}
          </div>
        </div>

        {/* Bouton CTA */}
          <button
          onClick={handleCTA}
          className={`w-full py-3 px-6 rounded-lg transition-colors flex items-center justify-center space-x-2 ${ctaButtonClass}`}
        >
          {ctaType === 'order' ? (
            <>
              <ShoppingCart size={18} />
              <span className="font-semibold">{ctaText || t('promotions.order')}</span>
            </>
          ) : (
            <>
              <ExternalLink size={18} />
              <span className="font-semibold">{ctaText || t('promotions.discover')}</span>
            </>
          )}
          </button>
      </div>
    </div>
  );
};

export default PromotionCard;
